/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { HeatDrop, HeatSeries } from "src/store/api/generatedApi";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import KeyboardDoubleArrowLeftRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowLeftRounded";
import KeyboardDoubleArrowRightRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowRightRounded";
import TrainRoundedIcon from "@mui/icons-material/TrainRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  VideoControlProvider,
  useVideoControlActions,
} from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { AllAnglesVideoPlayer } from "components/scrapEyeDataDashboard/VideoPlayer/components/AllAnglesVideoPlayer";
import { HotkeyProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { TimelineZoomProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/TimelineZoomContext";
import { useAreaLabelledEvents } from "components/scrapEyeLabelling/hooks/useAreaLabel";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import dayjs, { Dayjs } from "dayjs";
import { useTenantData } from "contexts/tenant";
import { v4 } from "uuid";
import { useHeatsSelection } from "components/strategyDashboard/heats/context/HeatsSelectionContext";
import { useHeatsData } from "components/strategyDashboard/heats/context/HeatsDataContext";
import {
  ProvideScrapEyeMetadata,
  useScrapEyeMetadata,
} from "contexts/scrapEyeMetadata";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { useLabellingLocations } from "hooks/useLabellingLocations";
import { useTenantTranslation } from "hooks/formatters";
import { useMaterialSymbol } from "hooks/useMaterialSymbol";
import { formatArea } from "components/identifyEventsTool/utils/formatArea";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { ToggleSelector } from "../../ToggleSelector";
import { DropGrouping } from "../timelineTab/HeatDropTimeline";
import { VideoOverlayIconButton } from "components/scrapEyeDataDashboard/VideoPlayer/components/VideoOverlayIconButton";
import { FloatingRailcarMap } from "./ReportedRailcarMap";
import { PLAYBACK_SCOPE } from "components/scrapEyeDataDashboard/VideoPlayer/constants";

const EXCLUDED_LOCATIONS = new Set(["house"]);

export const WatchHeatCell = ({ row }: { row: HeatSeries }) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();
  const videoStart = row.loading_start_timestamp ?? row.start_timestamp;
  const videoEnd =
    row.loading_end_timestamp ?? row.tap_timestamp ?? row.start_timestamp;

  const { data } = useHeatsData();
  const { selectedWatchHeatId, setSelectedWatchHeatId } = useHeatsSelection();

  const nextHeatId =
    data?.series[
      data?.series.findIndex((row_) => row_.heat_id === row.heat_id) + 1
    ]?.heat_id;
  const previousHeatId =
    data?.series[
      data?.series.findIndex((row_) => row_.heat_id === row.heat_id) - 1
    ]?.heat_id;

  const incrementHeatId =
    nextHeatId !== undefined
      ? () => {
          setSelectedWatchHeatId(nextHeatId);
        }
      : undefined;
  const decrementHeatId =
    previousHeatId !== undefined
      ? () => {
          setSelectedWatchHeatId(previousHeatId);
        }
      : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <IconButton
        aria-label={`${t("videoViewer")} ${row.heat_id}`}
        onClick={() => {
          setSelectedWatchHeatId(row.heat_id);
        }}
        color="primary"
        size="small"
        sx={{
          maxWidth: "28px",
          maxHeight: "28px",
          minWidth: "28px",
          minHeight: "28px",
          "&:hover": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
        }}
      >
        <VisibilityRoundedIcon />
      </IconButton>

      <Modal
        open={selectedWatchHeatId == row.heat_id}
        onClose={() => {
          setSelectedWatchHeatId(null);
        }}
      >
        <Box
          role="dialog"
          aria-label={row.heat_id}
          sx={{
            position: "absolute",
            width: "95vw",
            maxHeight: "90vh",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "background.paper",
            padding: 2,
            overflowY: "auto",
          }}
        >
          <IconButton
            aria-label={t("close")}
            onClick={() => {
              setSelectedWatchHeatId(null);
            }}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseRoundedIcon />
          </IconButton>
          <ProvideScrapEyeMetadata>
            <WatchHeatModalContent
              heatId={row.heat_id}
              furnace={row.eaf}
              start={videoStart}
              end={videoEnd}
              drops={row.drops}
              incrementHeatId={incrementHeatId}
              decrementHeatId={decrementHeatId}
              closeButton={
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSelectedWatchHeatId(null);
                  }}
                >
                  {t("close")}
                </Button>
              }
            />
          </ProvideScrapEyeMetadata>
        </Box>
      </Modal>
    </Box>
  );
};

interface Props {
  heatId: string;
  furnace: string | null;
  start: string;
  end: string;
  drops: HeatDrop[];
  incrementHeatId: (() => void) | undefined;
  decrementHeatId: (() => void) | undefined;
  closeButton: ReactNode;
}

const WatchHeatModalContent = ({
  heatId,
  furnace,
  start,
  end,
  drops,
  incrementHeatId,
  decrementHeatId,
  closeButton,
}: Props) => {
  const { t } = useTenantTranslation();
  const { local_timezone: localTimezone } = useTenantData();
  const localStart = useMemo(
    () => dayjs(start).tz(localTimezone),
    [start, localTimezone]
  );
  const loadingWindowSeconds = useMemo(
    () => dayjs(end).diff(dayjs(start), "second", true),
    [start, end]
  );

  const { locations: labellingLocations } = useLabellingLocations();

  const locations = useMemo(() => {
    const available = labellingLocations.filter(
      (l) => !EXCLUDED_LOCATIONS.has(l.name)
    );
    const watching =
      furnace === null
        ? []
        : available.filter((l) => (l.furnaces ?? []).includes(furnace));
    return (watching.length > 0 ? watching : available).map((l) => l.name);
  }, [labellingLocations, furnace]);

  const {
    selectedWatchLocation,
    setSelectedWatchLocation,
    dropGrouping,
    setDropGrouping,
  } = useHeatsSelection();
  const { drop_areas } = useScrapEyeMetadata();

  const availableGroupings = useMemo((): Partial<
    Record<DropGrouping, string>
  > => {
    const craneNames = new Set(
      drops.map((d) => d.location_name).filter((n): n is string => n !== null)
    );
    const areaNames = new Set(
      drops
        .map(
          (d) =>
            d.consumption_area ??
            (drop_areas.includes(d.to_area)
              ? d.to_area
              : drop_areas.includes(d.from_area)
                ? d.from_area
                : null)
        )
        .filter((a): a is string => a !== null)
    );
    const result: Partial<Record<DropGrouping, string>> = {
      combined: "combined",
    };
    if (craneNames.size >= 1) result.byCrane = "byCrane";
    if (areaNames.size >= 1) result.byConsumptionArea = "byConsumptionArea";
    return result;
  }, [drops, drop_areas]);

  useEffect(() => {
    if (!(dropGrouping in availableGroupings)) {
      setDropGrouping("combined");
    }
  }, [dropGrouping, availableGroupings, setDropGrouping]);
  const location =
    selectedWatchLocation !== null && locations.includes(selectedWatchLocation)
      ? selectedWatchLocation
      : (locations[0] ?? "crane");
  const { getMaterialSymbol } = useMaterialSymbol();
  const hasCraneLanes = useIsBehindFeatureFlag("heat_video_crane_lanes");
  const hasRailcarMap = useIsBehindFeatureFlag("railcar_area_materials");
  const [isRailcarMapOpen, setIsRailcarMapOpen] = useState(false);
  const sharedRailcarMapLocation = useMemo(() => {
    const furnacesKey = (name: string) =>
      (
        labellingLocations.find((candidate) => candidate.name === name)
          ?.furnaces ?? []
      ).join();
    const selectedFurnaces = furnacesKey(location);
    return selectedFurnaces === ""
      ? location
      : (locations.find((name) => furnacesKey(name) === selectedFurnaces) ??
          location);
  }, [labellingLocations, locations, location]);

  const withAreaLabels = useAreaLabelledEvents(location);
  const events = useMemo(() => {
    // True when a drop was made by a crane other than the selected camera feed.
    // Drops with unknown location (null) are shown at full colour.
    const isDimmed = (location_name: string | null) =>
      location_name !== null && location_name !== location;

    if (dropGrouping === "byCrane") {
      // All crane lanes visible; events from the non-selected crane are greyed out.
      return withAreaLabels(
        drops.map(
          ({
            material_name,
            drop_timestamp,
            to_area,
            mass,
            location_name,
          }) => ({
            id: v4(),
            start: dayjs(drop_timestamp).subtract(5, "second"),
            end: dayjs(drop_timestamp).add(5, "second"),
            eventType: "drop" as const,
            material: material_name,
            shortLabel: getMaterialSymbol(material_name),
            area: to_area,
            recordedMass: mass,
            lane: location_name ?? "unknown",
            laneLabel: formatArea(location_name ?? "unknown"),
            cameraLocation: location_name ?? undefined,
            hoverLabel:
              location_name !== null ? formatArea(location_name) : undefined,
            ...(isDimmed(location_name) ? { opacity: 0.3 } : {}),
          })
        )
      );
    }

    if (dropGrouping === "byConsumptionArea") {
      // Lanes split by area; individual drops from other cranes are greyed out.
      // Drops land in to_area; removals originate from from_area — coalesce to
      // whichever represents the consumption area for this event direction.
      return withAreaLabels(
        drops
          .map(
            ({
              material_name,
              drop_timestamp,
              consumption_area,
              from_area,
              to_area,
              mass,
              location_name,
            }) => {
              const consumptionArea =
                consumption_area ??
                (drop_areas.includes(to_area)
                  ? to_area
                  : drop_areas.includes(from_area)
                    ? from_area
                    : null);
              if (consumptionArea === null) return null;
              return {
                id: v4(),
                start: dayjs(drop_timestamp).subtract(5, "second"),
                end: dayjs(drop_timestamp).add(5, "second"),
                eventType: "drop" as const,
                material: material_name,
                shortLabel: getMaterialSymbol(material_name),
                area: to_area,
                recordedMass: mass,
                lane: consumptionArea,
                laneLabel: formatArea(consumptionArea),
                cameraLocation: location_name ?? undefined,
                hoverLabel:
                  location_name !== null
                    ? formatArea(location_name)
                    : undefined,
                ...(isDimmed(location_name) ? { opacity: 0.3 } : {}),
              };
            }
          )
          .filter((e): e is NonNullable<typeof e> => e !== null)
      ).map((e) => ({ ...e, areaShortLabel: "" }));
    }

    // combined — original behaviour: filter by camera location(s)
    const laneLocations =
      hasCraneLanes && locations.length > 1 ? locations : [location];
    return withAreaLabels(
      laneLocations.flatMap((laneLocation) =>
        drops
          .filter((drop) => drop.location_name === laneLocation)
          .map(({ material_name, drop_timestamp, to_area, mass }) => ({
            id: v4(),
            start: dayjs(drop_timestamp).subtract(5, "second"),
            end: dayjs(drop_timestamp).add(5, "second"),
            eventType: "drop" as const,
            material: material_name,
            shortLabel: getMaterialSymbol(material_name),
            area: to_area,
            recordedMass: mass,
            lane: laneLocations.length > 1 ? laneLocation : undefined,
            laneLabel:
              laneLocations.length > 1 ? formatArea(laneLocation) : undefined,
            cameraLocation: laneLocations.length > 1 ? laneLocation : undefined,
            hoverLabel: formatArea(laneLocation),
          }))
      )
    );
  }, [
    drops,
    drop_areas,
    dropGrouping,
    hasCraneLanes,
    locations,
    location,
    getMaterialSymbol,
    withAreaLabels,
  ]);

  return (
    <ProvideScrapEyeMetadata>
      <Stack>
        <Stack
          direction="row"
          justifyContent="space-around"
          alignItems="center"
        >
          <IconButton
            size="large"
            onClick={decrementHeatId}
            disabled={decrementHeatId === undefined}
          >
            <KeyboardDoubleArrowLeftRoundedIcon />
          </IconButton>
          <Stack alignItems="center" spacing={1}>
            <Typography variant="h5">{heatId}</Typography>
            {locations.length > 1 && (
              <ToggleButtonGroup
                value={location}
                exclusive
                onChange={(_, newLocation) => {
                  if (newLocation !== null)
                    setSelectedWatchLocation(newLocation as string);
                }}
                size="small"
              >
                {locations.map((loc) => (
                  <ToggleButton key={loc} value={loc}>
                    {loc}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}
          </Stack>
          <IconButton
            size="large"
            onClick={incrementHeatId}
            disabled={incrementHeatId === undefined}
          >
            <KeyboardDoubleArrowRightRoundedIcon />
          </IconButton>
        </Stack>
        <Stack direction="row" justifyContent="center" sx={{ py: 0.5 }}>
          <ToggleSelector<DropGrouping>
            selectedValue={dropGrouping}
            setSelectedValue={setDropGrouping}
            availableValueLabels={
              availableGroupings as Record<DropGrouping, string>
            }
          />
        </Stack>
        <HotkeyProvider>
          <VideoControlProvider>
            <TimelineZoomProvider
              key={location}
              fullDuration={loadingWindowSeconds}
              videoStart={localStart}
            >
              <WatchHeatVideoPlayer
                location={location}
                startIso={start}
                endIso={end}
                events={events}
                videoStart={localStart}
                onLocationChange={setSelectedWatchLocation}
                closeButton={closeButton}
                topRightButtons={
                  hasRailcarMap ? (
                    <Tooltip title={t("railcars")}>
                      <VideoOverlayIconButton
                        aria-label={t("railcars")}
                        aria-pressed={isRailcarMapOpen}
                        onClick={() => {
                          setIsRailcarMapOpen(!isRailcarMapOpen);
                        }}
                      >
                        <TrainRoundedIcon />
                      </VideoOverlayIconButton>
                    </Tooltip>
                  ) : undefined
                }
              />
            </TimelineZoomProvider>
            {isRailcarMapOpen ? (
              <FloatingRailcarMap
                location={sharedRailcarMapLocation}
                startIso={start}
                endIso={end}
                videoStart={localStart}
                onClose={() => {
                  setIsRailcarMapOpen(false);
                }}
              />
            ) : null}
          </VideoControlProvider>
        </HotkeyProvider>
      </Stack>
    </ProvideScrapEyeMetadata>
  );
};

interface WatchHeatVideoPlayerProps {
  location: string;
  startIso: string;
  endIso: string;
  events: ScrapEyesEvent[];
  videoStart: Dayjs;
  onLocationChange: (location: string) => void;
  closeButton: ReactNode;
  topRightButtons: ReactNode;
}

const WatchHeatVideoPlayer = ({
  location,
  startIso,
  endIso,
  events,
  videoStart,
  onLocationChange,
  closeButton,
  topRightButtons,
}: WatchHeatVideoPlayerProps) => {
  const { seekTo } = useVideoControlActions();

  const watchEvent = useCallback(
    (eventId: string) => {
      const event = events.find((candidate) => candidate.id === eventId);
      if (!event) return;
      seekTo(event.start.diff(videoStart, "second", true));
      const cameraTarget = event.cameraLocation;
      if (cameraTarget !== undefined && cameraTarget !== location)
        onLocationChange(cameraTarget);
    },
    [events, seekTo, videoStart, location, onLocationChange]
  );

  return (
    <AllAnglesVideoPlayer
      hotkeyScope={PLAYBACK_SCOPE}
      location={location}
      startIso={startIso}
      endIso={endIso}
      events={events}
      videoStart={videoStart}
      onEventClick={watchEvent}
      colorEventsBy="material"
      materialNameSource="raw"
      showShortLabels
      shortLabelDetail="area"
      highlightedLane={location}
      gridLayout={{ maxTileHeight: "50vh" }}
      centerActionButtons={closeButton}
      topRightButtons={topRightButtons}
    />
  );
};
